import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import apiService from '../services/apiService';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAuthExpired, setIsAuthExpired] = useState(false);
  const tokenCheckIntervalRef = useRef(null);
  const logoutTimeoutRef = useRef(null);

  // Decode JWT token to get expiration time
  const getTokenExpiration = () => {
    const token = apiService.getAccessToken();
    if (!token) return null;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const decoded = JSON.parse(jsonPayload);
      return decoded.exp ? decoded.exp * 1000 : null; // Convert to milliseconds
    } catch (err) {
      console.warn('Error decoding token:', err);
      // If token is malformed, treat as invalid/expired
      return 0;
    }
  };

  // Check if token is expired - memoized to prevent infinite loops
  const isTokenExpired = useCallback(() => {
    const expTime = getTokenExpiration();
    if (!expTime) return false;
    return Date.now() >= expTime;
  }, []); // No dependencies - uses getTokenExpiration which reads from localStorage

  // Handle token expiration
  const handleTokenExpired = useCallback(async () => {
    console.warn('Token expired - logging out user');
    setIsAuthExpired(true);
    toast.warning('Your session has expired. Please log in again.');

    // Clear auth state
    setUser(null);
    apiService.clearTokens();

    // Clear intervals and timeouts
    if (tokenCheckIntervalRef.current) {
      clearInterval(tokenCheckIntervalRef.current);
    }
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
    }

    // Redirect to home after a short delay
    logoutTimeoutRef.current = setTimeout(() => {
      navigate('/');
      setIsAuthExpired(false);
    }, 1500);
  }, [navigate]);

  // Setup token expiration monitoring
  const setupTokenMonitoring = useCallback(() => {
    // Clear existing interval
    if (tokenCheckIntervalRef.current) {
      clearInterval(tokenCheckIntervalRef.current);
    }

    // Check token every 30 seconds
    tokenCheckIntervalRef.current = setInterval(() => {
      if (isTokenExpired()) {
        handleTokenExpired();
      }
    }, 30000);
  }, [handleTokenExpired, isTokenExpired]);

  // On mount, check if user is authenticated by calling /auth/me
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Pass skipErrorHandler to prevent the interceptor's redirect on 401
        const me = await apiService.getMe({ skipErrorHandler: true });
        setUser(me);
        setupTokenMonitoring();
      } catch (err) {
        // This block will now handle the 401 gracefully
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();

    return () => {
      if (tokenCheckIntervalRef.current) {
        clearInterval(tokenCheckIntervalRef.current);
      }
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const refreshUser = async () => {
    try {
      const me = await apiService.getUserProfile();
      setUser(me);
      setupTokenMonitoring(); // Restart token monitoring
      return me;
    } catch (err) {
      // If refresh fails due to auth, handle expiration
      if (err.status === 401) {
        await handleTokenExpired();
      }
      setUser(null);
      throw err;
    }
  };

  const login = async (email, password) => {
    setLoading(true);
    setError('');
    setIsAuthExpired(false);
    try {
      const response = await apiService.login(email, password);
      //console.log('Backend login response:', response);
      // Only set user if tokens are present
      if (response.user) {
        //console.log('AuthContext: Setting user:', response.user);
        setUser(response.user);
        setupTokenMonitoring(); // Start monitoring token expiration
      } else {
        //console.log('AuthContext: No user found in response:', response);
        setUser(null);
        setError('Login failed: No tokens received');
        return null;
      }
      return response;
    } catch (err) {
      setError(err.message || 'Login failed');
      setUser(null);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (form) => {
    setLoading(true);
    setError('');
    setIsAuthExpired(false);
    try {
      const data = await apiService.register(form);
      return data;
    } catch (err) {
      setError(err.message || 'Signup failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    setError('');
    try {
      await apiService.logout();
      // Clear auth state
      setUser(null);
      setIsAuthExpired(false);
      apiService.clearTokens();

      // Clear intervals and timeouts
      if (tokenCheckIntervalRef.current) {
        clearInterval(tokenCheckIntervalRef.current);
      }
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }

      toast.success('Logged out successfully');
      navigate('/');
    } catch (err) {
      setError(err.message || 'Logout failed');
      // Even if logout API fails, clear local state
      setUser(null);
      apiService.clearTokens();
    } finally {
      setLoading(false);
    }
  };

  // Derive isAdmin from user role
  const isAdmin = user?.role === 'admin' || user?.is_admin === true;

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      login,
      register,
      logout,
      refreshUser,
      isAdmin,
      isAuthExpired,
      isTokenExpired
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext); 